const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const SESSION_EXPIRED_KEY = "sessionExpired";

let sessionRedirectStarted = false;

export default API_BASE_URL;

function getStoredToken() {
  const storedToken = localStorage.getItem("token");

  if (!storedToken) {
    return null;
  }

  try {
    return JSON.parse(storedToken);
  } catch {
    return storedToken.replaceAll('"', "");
  }
}

function buildUrl(route, id = null) {
  const baseUrl = `${API_BASE_URL}/${route}`;

  if (id === null || id === undefined) {
    return baseUrl;
  }

  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}id=${encodeURIComponent(id)}`;
}

async function readResponseBody(response) {
  const responseText = await response.text();

  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return {
      isSuccess: false,
      message: responseText,
    };
  }
}

function isAuthenticationFailure(response, responseBody) {
  if (response.status === 401) {
    return true;
  }

  if (response.status !== 422) {
    return false;
  }

  const message = String(
    responseBody?.msg ??
      responseBody?.message ??
      responseBody?.body ??
      ""
  ).toLowerCase();

  return (
    message.includes("token") ||
    message.includes("jwt") ||
    message.includes("signature") ||
    message.includes("authorization")
  );
}

function redirectToLoginOnce() {
  if (sessionRedirectStarted) {
    return;
  }

  sessionRedirectStarted = true;

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");

  window.location.replace("/login");
}

function waitForLoginRedirect() {
  // The page is navigating to /login. Keeping authenticated calls pending
  // prevents individual components from showing duplicate error alerts.
  return new Promise(() => {});
}

async function apiRequest(
  route,
  {
    method = "GET",
    id = null,
    object = undefined,
    requiresToken = false,
  } = {}
) {
  try {
    const headers = {
      "Content-Type": "application/json",
    };

    if (requiresToken) {
      const token = getStoredToken();

      if (!token) {
        redirectToLoginOnce();
        return waitForLoginRedirect();
      }

      headers.Authorization = `Bearer ${token}`;
    }

    const requestOptions = {
      method,
      headers,
    };

    if (object !== undefined) {
      requestOptions.body = JSON.stringify(object);
    }

    const response = await fetch(buildUrl(route, id), requestOptions);
    const responseBody = await readResponseBody(response);

    if (
      requiresToken &&
      isAuthenticationFailure(response, responseBody)
    ) {
      redirectToLoginOnce();
      return waitForLoginRedirect();
    }

    if (responseBody?.msg) {
      return {
        isSuccess: false,
        message: responseBody.msg,
      };
    }

    if (!response.ok) {
      return {
        isSuccess: false,
        message:
          responseBody?.message ||
          responseBody?.body ||
          `Request failed with status ${response.status}.`,
      };
    }

    return responseBody;
  } catch (error) {
    return {
      isSuccess: false,
      message: error?.message || "The request could not be completed.",
    };
  }
}

export async function getAll(route, id) {
  return apiRequest(route, {
    method: "GET",
    id,
  });
}

export async function getAllWithToken(route, id) {
  return apiRequest(route, {
    method: "GET",
    id,
    requiresToken: true,
  });
}

export async function post(route, object) {
  return apiRequest(route, {
    method: "POST",
    object,
  });
}

export async function postWithToken(route, object) {
  return apiRequest(route, {
    method: "POST",
    object,
    requiresToken: true,
  });
}

export async function put(route, object) {
  return apiRequest(route, {
    method: "PUT",
    object,
  });
}

export async function putWithToken(route, object) {
  return apiRequest(route, {
    method: "PUT",
    object,
    requiresToken: true,
  });
}

export async function delWithToken(route, id) {
  return apiRequest(`${route}/${id}`, {
    method: "DELETE",
    requiresToken: true,
  });
}
